import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaPrivatePartySalePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Private Party Vehicle Sale</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Private Party Vehicle Sale Guide</h1>
        <p className="text-obsidian-300">What buyers and sellers need to know when selling a vehicle privately in Nevada.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Seller Checklist</h2>
          <ul className="space-y-3">
            {[
              'Sign the back of the title in the seller signature area (both owners if "AND" on title)',
              'Fill in the odometer reading on the title (required for vehicles under 10 years old)',
              'Complete a Bill of Sale (Form VP 104) with purchase price, date, VIN, and both parties\' info',
              'Remove your license plates — plates stay with you, not the vehicle',
              'Notify the Nevada DMV of the sale within 10 days to protect yourself from liability',
              'Do NOT sign a blank title — always fill in buyer information before signing',
              'Provide any maintenance records, smog certificates, or other relevant documents',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Buyer Checklist</h2>
          <ul className="space-y-3">
            {[
              'Verify the seller\'s name matches the name on the title',
              'Check for any liens on the vehicle (lienholder listed on title)',
              'Ensure the VIN on the title matches the VIN on the vehicle',
              'Get a signed Bill of Sale with the actual purchase price',
              'Obtain Nevada insurance before driving the vehicle',
              'Complete title transfer and registration within 30 days of purchase',
              'Get a VIN inspection if the vehicle has never been registered in Nevada',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Title Signing Rules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: '"AND" on Title', desc: 'Both owners must sign the title for the sale to be valid.' },
              { title: '"OR" on Title', desc: 'Only one owner needs to sign the title.' },
              { title: 'Lienholder on Title', desc: 'The lien must be released before the vehicle can be sold. Contact the lender.' },
              { title: 'Out-of-State Title', desc: 'Follow the signing requirements of the state that issued the title.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Buying or Selling a Vehicle?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services handles title transfers and registration for private party sales in Las Vegas.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaPrivatePartySalePage;
